import {z} from 'zod'

import {
  BlockMediaGalleryVisualizationEnum,
  CreateOrUpdateBlockMediaGalleryDocument, XcMediaRefFragment,
} from '@sdox/api'

import {updateMetaSchema} from '../../utils/getUpdateMetaFromFieldValues'
import {createFormConfig} from '../../utils/createFormConfig'
import {i18nValidation} from '../../../../../i18n'

import type {XcNwBlock_BlockMediaGalleryType_Fragment} from '@sdox/api'
import type {BlockMediaGalleryInput,
  CreateOrUpdateBlockMediaGalleryMutation} from '@sdox/api'

type UpdateGalleryValidatorSchema = z.infer<typeof updateGalleryValidatorSchema>

const updateGalleryValidatorSchema = z.object({
  data: z.object({
    visualization: z.nativeEnum(BlockMediaGalleryVisualizationEnum),
    images: z.object({
      refs: z.array(
        z.object({
          id: z.string().min(1),
          fileHash: z.string().min(1),
        }),
        // We need an extra error message, because currently images is undefined when form is initialized
        {message: i18nValidation.noImagesSelected},
      ).nonempty({message: i18nValidation.noImagesSelected}),
      // Text is mapped from media ref id to (optional) string
      text: z.record(
        z.string(),
        z.string().nullish(),
      ),
    }),
  }),
  meta: updateMetaSchema,
})

export const galleryConfig = createFormConfig<
  UpdateGalleryValidatorSchema,
  CreateOrUpdateBlockMediaGalleryMutation,
  BlockMediaGalleryInput,
  XcNwBlock_BlockMediaGalleryType_Fragment
>({
  mutationOptions: {
    mutation: CreateOrUpdateBlockMediaGalleryDocument,
  },
  getDefaultValues: (block) => {
    const text: Record<string, string | null> = {}
    // We need to use _id, because id is a stringified number, react-hook-form would treat is as an array
    // instead of an object
    block.mediaGalleryItems.forEach(item => text[`_${item.image.id}`] = item.text)

    return {
      data: {
        visualization: block.mediaGalleryVisualization,
        images: {
          refs: block.mediaGalleryItems.map(item => item.image),
          text,
        },
      },
    }
  },
  fieldMapper: (fieldValues) => {
    const input: BlockMediaGalleryInput = {
      visualization: fieldValues.data.visualization,
      items: fieldValues.data.images.refs.map((image) => ({
        fileHash: image.fileHash,
        // We need to use _id, because id is a stringified number, react-hook-form would treat is as an array
        // instead of an object
        text: fieldValues.data.images.text[`_${image.id}`] ?? null,
      })),
    }
    return input
  },
  validatorSchema: updateGalleryValidatorSchema,
  inputFields: [
    {
      label: 'visualization',
      fieldName: 'data.visualization',
      inputType: 'select',
      defaultValue: BlockMediaGalleryVisualizationEnum.Gallery,
      options: [
        {label: BlockMediaGalleryVisualizationEnum.Gallery, value: BlockMediaGalleryVisualizationEnum.Gallery},
        {label: BlockMediaGalleryVisualizationEnum.Thumbnails, value: BlockMediaGalleryVisualizationEnum.Thumbnails},
        {label: BlockMediaGalleryVisualizationEnum.MediaStream, value: BlockMediaGalleryVisualizationEnum.MediaStream},
      ],
      required: true,
    },
    {
      label: 'Images',
      fieldName: 'data.images',
      inputType: 'fileWithText',
      defaultValue: [],
      required: true,
    },
  ],
})
